/**
 * PostForMe redirects the social-OAuth flow back to our origin with the result
 * encoded in the query string, e.g.
 *
 *   /app?provider=linkedin&projectId=proj_x&isSuccess=false&accountIds=
 *        &failedAccountIds=spc_x&error=External+Id+already+exists...
 *
 * On a FAILED connect the backend receives no webhook (PostForMe only fires
 * `social.account.created` on success), so this redirect is the ONLY failure
 * signal — the FE has to surface it instead of silently continuing.
 * See LIL-383 (this work) / LIL-267 (the "connect does nothing" root cause).
 */
export interface OAuthConnectResult {
  isSuccess: boolean;
  provider: string | null;
  error: string | null;
  failedAccountIds: string[];
}

/**
 * Marker PostForMe returns when the social account is already linked to a
 * different Lili profile. It binds one `external_id` (= our user id) per social
 * account and rejects re-linking it to another user.
 */
const ALREADY_LINKED_MARKER = 'External Id already exists';

/**
 * Parse PostForMe's redirect query params into a typed result, or return null
 * if the params don't carry a result signal (so unrelated URLs are ignored).
 * Accepts a raw search string or a URLSearchParams.
 */
export function parseOAuthResult(search: string | URLSearchParams): OAuthConnectResult | null {
  const params = typeof search === 'string' ? new URLSearchParams(search) : search;

  // Treat this as an OAuth-connect redirect only if it carries a result signal.
  // `isSuccess` is PostForMe's; `status` is the legacy popup-callback param.
  if (!params.has('isSuccess') && !params.has('status')) return null;

  const isSuccess = params.get('isSuccess') === 'true' || params.get('status') === 'success';

  return {
    isSuccess,
    provider: params.get('provider'),
    error: params.get('error'),
    failedAccountIds: (params.get('failedAccountIds') ?? '')
      .split(',')
      .map(id => id.trim())
      .filter(Boolean),
  };
}

/** Display label for a provider key (e.g. `linkedin` → `LinkedIn`). */
export function formatProvider(provider: string | null): string {
  if (!provider) return 'account';
  const labels: Record<string, string> = {
    linkedin: 'LinkedIn',
    x: 'X',
    facebook: 'Facebook',
    instagram: 'Instagram',
  };
  const key = provider.toLowerCase();
  return labels[key] ?? provider.charAt(0).toUpperCase() + provider.slice(1);
}

/**
 * Build a user-facing message for a failed connect. Special-cases the
 * "already linked to another profile" error (the LIL-267 root cause); falls
 * back to the decoded backend `error` for anything else.
 */
export function oauthFailureMessage(result: OAuthConnectResult): string {
  const provider = formatProvider(result.provider);

  if (result.error && result.error.includes(ALREADY_LINKED_MARKER)) {
    return `This ${provider} account is already connected to another Lili profile. Disconnect it there first, then try again.`;
  }

  if (result.error) return result.error;

  return `Couldn't connect ${provider}. Please try again.`;
}

/** `error_type` for channel_connect_failed; never the raw provider text. */
export function connectErrorType(
  result: OAuthConnectResult
): 'already_linked' | 'user_cancelled' | 'api_error' {
  if (result.error?.includes(ALREADY_LINKED_MARKER)) return 'already_linked';
  if (result.error && /denied|cancel/i.test(result.error)) return 'user_cancelled';
  return 'api_error';
}

/** `social:account-rejected`: the backend refused a connection the provider completed. */
export interface AccountRejectedPayload {
  /** The platform that was refused. */
  platform: string;
  /** The platform the trial is locked to. */
  allowedPlatform: string;
}

/**
 * Copy for a plan refusal. Not `oauthFailureMessage`: PostForMe reports success
 * here, and only our webhook handler turned the account away.
 */
export function planRejectionMessage(payload: AccountRejectedPayload): string {
  const allowed = formatProvider(payload.allowedPlatform);

  if (payload.platform === payload.allowedPlatform) {
    return `Your free trial is linked to your ${allowed} account. Upgrade to connect a different account.`;
  }

  return `Your free trial is linked to ${allowed}. Upgrade to connect ${formatProvider(payload.platform)}.`;
}
